import { Fragment, useMemo, useState, type ReactNode } from "react";
import { View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  PROGRESS_LAYOUT_SURFACE,
  trailingRange,
  type ProgressWidgetId,
  type StoredExerciseTracker,
  type StoredWidgetLayout,
  type WeekStreak,
} from "@heraion/shared";

import { WidgetColumns } from "../Home/WidgetColumns";
import { WidgetConfigSheet } from "../Home/WidgetConfigSheet";
import { useWidgetLayout, type SaveWidgetLayout } from "../Home/useWidgetLayout";
import { Button } from "../primitives/Button";
import {
  nowToTheMinute,
  PROGRESS_WEEK_BARS,
  PROGRESS_WINDOW_DAYS,
  type WindowedProgressWidget,
} from "./progressWindow";
import { ProgressReportSheet, type ShareReportImage } from "./ProgressReportSheet";
import { MuscleCoverageCard } from "./MuscleCoverageCard";
import { E1RMTrendsPanel } from "./E1RMTrendsPanel";
import { ExerciseTrackerPanel } from "./ExerciseTrackerPanel";
import { ExerciseFrequencyPanel } from "./ExerciseFrequencyPanel";
import { IntensityHeatmapPanel } from "./IntensityHeatmapPanel";
import { MovementBalancePanel } from "./MovementBalancePanel";
import { PainVolumePanel } from "./PainVolumePanel";
import { RepsAtLoadTrendsPanel } from "./RepsAtLoadTrendsPanel";
import { RirAtLoadPanel } from "./RirAtLoadPanel";
import { ProgressCollapsibleWidget } from "./ProgressCollapsibleWidget";
import { ProgressPrTimelineCard } from "./ProgressPrTimelineCard";
import { ProgressRhythmCard } from "./ProgressRhythmCard";
import { ProgressWeekStreakCard } from "./ProgressWeekStreakCard";
import {
  PROGRESS_WIDGET_COLUMN,
  progressWidgetLabel,
} from "./progressWidgetCatalog";
import { parseRhythmKind, type SessionsRecap } from "./progressMath";
import { makeProgressViewStyles } from "./progressViewStyles";
import { flaggedPainMuscles } from "./tierOneMath";
import type { ProgressDeps } from "./types";
import type { SaveExerciseTracker } from "./useExerciseTracker";
import { useAccountPref } from "../useAccountPref";
import { useBreakpoint } from "../useBreakpoint";

const columnOf = (id: ProgressWidgetId) => PROGRESS_WIDGET_COLUMN[id];

// THE Progress surface, one tree for mobile's Progress tab AND the web-rnw
// console. PG-57: a widget surface like Home. The athlete orders, hides and
// collapses the widgets (Profile.progressLayout); a hidden widget is never
// mounted, and a collapsed one mounts its panel only once opened, so neither
// fetches. The data-heavy panels self-fetch through deps.apiFetch; the host
// owns the scroll container (mobile adds pull-to-refresh), passes the session
// summary it already fetches, and mounts the volume-by-tag card as a slot
// (host data wiring, the SAME shared `ProgressRecordsCard` on both).
// PG-64: no period selector. Each widget shows its own fixed window
// (progressWindow.ts); a free period, the past included, is the report's job,
// opened from here.
export function ProgressView({
  deps,
  rhythm,
  rhythmKindKey,
  weekStreak,
  summaryError,
  formatDate,
  locale,
  recordsSlot,
  progressLayout,
  onSaveProgressLayout,
  exerciseTracker,
  onSaveExerciseTracker,
  isPremium,
  sheetInsets,
  onShareReportImage,
  onShareReportError,
}: {
  deps: ProgressDeps;
  /** summarizeSessions' stats and calendar-week counts (oldest first, this
   *  week last) per kind of session; Rhythm shows the last PROGRESS_WEEK_BARS
   *  weeks of the kind chosen. */
  rhythm: SessionsRecap["rhythm"];
  /** #321: the account's key for that choice (mobile currentUserScopedKey,
   *  web useAccountKey); null keeps Strength and saves nothing. */
  rhythmKindKey: string | null;
  /** PG-58: computeWeekStreak over the same sessions; null while loading. */
  weekStreak: WeekStreak | null;
  summaryError: string | null;
  /** Format the last-session date in the host's preferred style. */
  formatDate: (iso: string, locale: string) => string;
  locale: string;
  /** Volume-by-tag mini-charts, the shared `ProgressRecordsCard`, host-fed. */
  recordsSlot: ReactNode;
  /** Stored layout (Profile.progressLayout); absent = the default Progress. */
  progressLayout: StoredWidgetLayout | undefined;
  /** Host write seam (mobile patchProfile, web useProfileDoc). */
  onSaveProgressLayout: SaveWidgetLayout;
  /** #389: Profile.exerciseTracker, the tracker's exercises, stats, range. */
  exerciseTracker?: StoredExerciseTracker;
  /** Its host write seam; absent, the choice holds for the visit only. */
  onSaveExerciseTracker?: SaveExerciseTracker;
  /** #392: the host's isPremium(profile); absent, the tracker is free. */
  isPremium?: boolean;
  /** Safe-area insets for the config page sheet; web passes none. */
  sheetInsets?: { top: number; bottom: number };
  /** PG-64: the report's PNG to the platform (mobile share sheet, web share
   *  or download). Absent: the report has no Share button. */
  onShareReportImage?: ShareReportImage;
  onShareReportError?: (message: string) => void;
}) {
  const { theme } = deps;
  const { t } = useTranslation();
  const { wide } = useBreakpoint();
  const styles = makeProgressViewStyles(theme);
  const progress = useWidgetLayout(PROGRESS_LAYOUT_SURFACE, {
    stored: progressLayout,
    save: onSaveProgressLayout,
  });
  const [configOpen, setConfigOpen] = useState(false);
  const openConfig = () => setConfigOpen(true);
  const [reportOpen, setReportOpen] = useState(false);

  // The windows end now, to the minute (nowToTheMinute).
  const nowMs = nowToTheMinute();
  const windowed = useMemo(() => {
    const at = (id: WindowedProgressWidget): ProgressDeps => ({
      ...deps,
      range: trailingRange(nowMs, PROGRESS_WINDOW_DAYS[id]),
    });
    return {
      e1rmTrends: at("e1rmTrends"),
      repsAtLoad: at("repsAtLoad"),
      rirAtLoad: at("rirAtLoad"),
      movementBalance: at("movementBalance"),
      painVolume: at("painVolume"),
      exerciseFrequency: at("exerciseFrequency"),
    };
  }, [deps, nowMs]);
  const [rhythmKind, setRhythmKind] = useAccountPref(
    rhythmKindKey,
    parseRhythmKind,
    "strength",
  );
  const shown = rhythm?.[rhythmKind] ?? null;
  const recentWeeks = (shown?.weeklyCounts ?? []).slice(-PROGRESS_WEEK_BARS);

  const widgets: Record<ProgressWidgetId, ReactNode> = {
    coverage: <MuscleCoverageCard deps={deps} locale={locale} />,
    e1rmTrends: <E1RMTrendsPanel {...windowed.e1rmTrends} />,
    // #389: its range is the athlete's choice, not a fixed window.
    exerciseTracker: (
      <ExerciseTrackerPanel
        {...deps}
        stored={exerciseTracker}
        onSave={onSaveExerciseTracker}
        isPremium={isPremium}
      />
    ),
    repsAtLoad: <RepsAtLoadTrendsPanel {...windowed.repsAtLoad} />,
    rirAtLoad: <RirAtLoadPanel {...windowed.rirAtLoad} />,
    movementBalance: <MovementBalancePanel {...windowed.movementBalance} />,
    // Nothing flagged in the pain map: nothing to show, so no card at all.
    painVolume:
      flaggedPainMuscles(deps.painMap).length > 0 ? (
        <PainVolumePanel {...windowed.painVolume} />
      ) : null,
    // PG-64 fetta 2: the last PROGRESS_WEEK_BARS calendar weeks, this week last.
    rhythm: (
      <ProgressRhythmCard
        theme={theme}
        summary={shown?.summary ?? null}
        summaryError={summaryError}
        rhythmCounts={recentWeeks}
        formatDate={formatDate}
        locale={locale}
        nowMs={nowMs}
        weekStartDay={deps.weekStartDay}
        kind={rhythmKind}
        onKind={setRhythmKind}
      />
    ),
    tagVolume: recordsSlot,
    // The whole history (its last 500 sets): no window.
    intensityHeatmap: <IntensityHeatmapPanel {...deps} />,
    exerciseFrequency: <ExerciseFrequencyPanel {...windowed.exerciseFrequency} />,
    // The latest MAXes: no window.
    prTimeline: (
      <ProgressPrTimelineCard deps={deps} formatDate={formatDate} locale={locale} />
    ),
    weekStreak: (
      <ProgressWeekStreakCard
        theme={theme}
        streak={weekStreak}
        summaryError={summaryError}
      />
    ),
  };

  const place = (id: ProgressWidgetId) => {
    const node = widgets[id];
    if (node === null) return null;
    if (!progress.layout.collapsed.includes(id)) {
      return (
        <Fragment key={id}>{node}</Fragment>
      );
    }
    return (
      <ProgressCollapsibleWidget
        key={id}
        theme={theme}
        label={progressWidgetLabel(t, id)}
      >
        {node}
      </ProgressCollapsibleWidget>
    );
  };

  // The door to the report, first on the page at every width.
  const report = (
    <Button
      theme={theme}
      variant="secondary"
      icon="chart"
      label={t("mobile.progress.report.open", { defaultValue: "Create a report" })}
      onPress={() => setReportOpen(true)}
    />
  );
  // The only door to the config sheet (PG-63: no hold on the cards). PG-62:
  // where Home has it, top right of the wide page, at the foot of the narrow
  // one (HomeView's twin).
  const customize = (
    <Button
      theme={theme}
      variant="ghost"
      icon="settings"
      label={t("mobile.progress.config.open", {
        defaultValue: "Customize Progress",
      })}
      onPress={openConfig}
      style={wide ? null : styles.customize}
    />
  );

  return (
    <>
      <View style={styles.header}>
        {report}
        {wide ? customize : null}
      </View>
      {/* Wide: the charts in main, the compact cards in the rail. These
          cards carry no margin, so the columns take the host's s4 gap. */}
      <WidgetColumns
        order={progress.layout.order}
        columnOf={columnOf}
        wide={wide}
        renderItem={place}
        gap={theme.spacing.s4}
      />
      {wide ? null : customize}
      <ProgressReportSheet
        deps={deps}
        visible={reportOpen}
        onClose={() => setReportOpen(false)}
        formatDate={formatDate}
        locale={locale}
        onShareImage={onShareReportImage}
        onShareError={onShareReportError}
        sheetInsets={sheetInsets}
      />
      <WidgetConfigSheet
        theme={theme}
        visible={configOpen}
        onClose={() => setConfigOpen(false)}
        title={t("mobile.progress.config.title", {
          defaultValue: "Customize Progress",
        })}
        saveErrorText={t("mobile.progress.config.saveError", {
          defaultValue: "Couldn't save your Progress layout. Try again.",
        })}
        surface={PROGRESS_LAYOUT_SURFACE}
        labelOf={(id) => progressWidgetLabel(t, id)}
        columnOf={columnOf}
        layout={progress.layout}
        onMove={progress.move}
        onHide={progress.hide}
        onShow={progress.show}
        onSetCollapsed={progress.setCollapsed}
        onReset={progress.reset}
        isDefault={progress.isDefault}
        saveFailed={progress.saveFailed}
        topInset={sheetInsets?.top}
        bottomInset={sheetInsets?.bottom}
      />
    </>
  );
}
